import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  useTheme,
  alpha,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Paper,
  Chip
} from "@mui/material";
import { Link } from "react-router-dom";
import CoinCard from "../components/CoinCard";
import ApplicationForm from "../components/ApplicationForm";
import { motion } from "framer-motion";
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FilterListIcon from '@mui/icons-material/FilterList';
import DiamondIcon from '@mui/icons-material/Diamond';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ForestIcon from '@mui/icons-material/Forest';

// Canadian coins data - Only authentic Canadian coins from Royal Canadian Mint
const canadianCoins = [
  // ========== ORIGINAL CANADIAN COINS ==========
  {
    id: 201,
    name: "Magnificent Maple Leaf Silver Coin",
    year: "2021",
    grade: "Proof 69",
    gradeNum: 69,
    price: "$2,850",
    priceNum: 2850,
    image: "/assets/images/canadacoins/2021 Canada 10 oz Silver $50 Magnificent Maple Leaves BU.png",
    description: "10 oz Canadian Silver Maple Leaf with magnificent finish. Stunning detail and presentation from the Royal Canadian Mint.",
    metal: "Silver",
    category: "canada",
    mint: "Royal Canadian Mint"
  },
  {
    id: 202,
    name: "Canadian 250 Dollars Gold Coin",
    year: "2016",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$12,500",
    priceNum: 12500,
    image: "/assets/images/jp/canada 250 dollars canada 1926 2016.jpg",
    description: "Commemorative gold coin celebrating Canadian heritage and the 150th anniversary of Confederation.",
    metal: "Gold",
    category: "canada",
    mint: "Royal Canadian Mint"
  },
  {
    id: 203,
    name: "Canadian Golden Eagle 2018 Gold Coin",
    year: "2018",
    grade: "MS69",
    gradeNum: 69,
    price: "$8,900",
    priceNum: 8900,
    image: "/assets/images/jp/Canadian Golden Eagle 2018 Gold Coin.png",
    description: "Stunning Canadian Golden Eagle gold coin with exceptional detail and pristine surfaces.",
    metal: "Gold",
    category: "canada",
    mint: "Royal Canadian Mint"
  },
  {
    id: 204,
    name: "Canadian Golden Eagle Gold Coin",
    year: "2019",
    grade: "MS68",
    gradeNum: 68,
    price: "$7,500",
    priceNum: 7500,
    image: "/assets/images/jp/canadian golden eagle gold coin .png",
    description: "Beautiful Canadian Golden Eagle design featuring Canada's national symbol.",
    metal: "Gold",
    category: "canada",
    mint: "Royal Canadian Mint"
  },
  {
    id: 205,
    name: "Maple Leaf Diamond Coin",
    year: "2020",
    grade: "Proof 70",
    gradeNum: 70,
    price: "POA",
    priceNum: null,
    image: "/assets/images/jp/Maple Leaf Diamonds  -249003_rev_1200.png",
    description: "Rare Maple Leaf coin featuring genuine diamond accents. Limited mintage with exceptional brilliance.",
    metal: "Gold",
    category: "canada",
    hasDiamond: true,
    mint: "Royal Canadian Mint"
  },

  // ========== NEW CANADIAN COINS WITH YOUR IMAGES ==========

  // Diamond Dust & Gilded Silver Maple Leaf
  {
    id: 206,
    name: "Diamond Dust & Gilded Silver Maple Leaf",
    year: "2025",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$850",
    priceNum: 850,
    image: "/assets/images/canadacoins/2025 Diamond Dust & Gilded 1oz Silver Maple Leaf Coin The Coin Chest.png",
    description: "Stunning 1oz silver Maple Leaf with diamond dust and gilded finish. Exclusive limited edition from RCM.",
    metal: "Silver",
    category: "canada",
    hasDiamond: true,
    mint: "Royal Canadian Mint"
  },

  // 1967 Canadian Confederation Gold Coin
  {
    id: 207,
    name: "Canadian Confederation Gold Coin",
    year: "1967",
    grade: "MS65",
    gradeNum: 65,
    price: "$22,500",
    priceNum: 22500,
    image: "/assets/images/canadacoins/1967 $20 Canadian Confederation Gold Coin.png",
    description: "Historic $20 gold coin celebrating Canada's 100th anniversary of Confederation. Centennial issue from RCM.",
    metal: "Gold",
    category: "canada",
    mint: "Royal Canadian Mint"
  },

  // 2012 Wildlife Series Cougar Silver Coin
  {
    id: 208,
    name: "Wildlife Series Cougar Silver Coin",
    year: "2012",
    grade: "Proof 69",
    gradeNum: 69,
    price: "$95",
    priceNum: 95,
    image: "/assets/images/canadacoins/2012 1 Oz Silver Wildlife Series Cougar Coin - AU Bullion Canada.png",
    description: "Magnificent cougar design from Canada's iconic wildlife series. Beautiful silver proof finish from RCM.",
    metal: "Silver",
    category: "canada",
    mint: "Royal Canadian Mint"
  },

  // 2016 Geometry in Art Polar Bear Silver Coin
  {
    id: 209,
    name: "Geometry in Art Polar Bear Silver Coin",
    year: "2016",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$120",
    priceNum: 120,
    image: "/assets/images/canadacoins/2016 Canadian $20 Geometry in ArtPolar Bear 1 oz Fine Silver Coloured .png",
    description: "Stunning coloured polar bear design featuring geometric art elements. 1oz fine silver from RCM.",
    metal: "Silver",
    category: "canada",
    mint: "Royal Canadian Mint"
  },

  // 2017 Canadian Voyageur 150th Anniversary Gold Coin
  {
    id: 210,
    name: "Voyageur 150th Anniversary Gold Coin",
    year: "2017",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$15,800",
    priceNum: 15800,
    image: "/assets/images/canadacoins/2017 1 oz Gold Canadian Voyageur 150th Anniversary Coins.png",
    description: "Special issue 1oz gold coin commemorating Canada's 150th birthday. Features classic Voyageur design.",
    metal: "Gold",
    category: "canada",
    mint: "Royal Canadian Mint"
  },

  // 2019 Apollo 11 Commemorative Coin (Canadian Mint issued this)
  {
    id: 211,
    name: "Apollo 11 50th Anniversary Commemorative Coin",
    year: "2019",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$2,800",
    priceNum: 2800,
    image: "/assets/images/canadacoins/2019 Apollo 11 50th Anniversary Commemorative Coin Images.png",
    description: "Commemorating the 50th anniversary of the moon landing. Stunning curved design from RCM.",
    metal: "Silver",
    category: "canada",
    mint: "Royal Canadian Mint"
  },

  // 2019 Celebration of Love Swarovski Silver Coin
  {
    id: 212,
    name: "Celebration of Love Swarovski Silver Coin",
    year: "2019",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$180",
    priceNum: 180,
    image: "/assets/images/canadacoins/2019 Celebration of Love - Pure Silver Coin made with Swarovski.png",
    description: "Beautiful heart-shaped silver coin featuring genuine Swarovski crystal elements. Limited mintage from RCM.",
    metal: "Silver",
    category: "canada",
    mint: "Royal Canadian Mint"
  },

  // 2021 Wolverine Silver Coin (from RCM)
  {
    id: 213,
    name: "Wolverine Silver Coin",
    year: "2021",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$95",
    priceNum: 95,
    image: "/assets/images/canadacoins/2021 1 oz Silver Tuvalu Wolverine Marvel Series Coins.png",
    description: "Stunning Wolverine design from the Royal Canadian Mint. Limited edition silver coin.",
    metal: "Silver",
    category: "canada",
    mint: "Royal Canadian Mint"
  },

  // 2021 Magnificent Maple Leaves 10oz
  {
    id: 214,
    name: "Magnificent Maple Leaves 10oz Silver Coin",
    year: "2021",
    grade: "BU",
    gradeNum: 68,
    price: "$450",
    priceNum: 450,
    image: "/assets/images/canadacoins/2021 Canada 10 oz Silver $50 Magnificent Maple Leaves BU.png",
    description: "Impressive 10oz silver coin featuring magnificent maple leaves design. Brilliant uncirculated finish from RCM.",
    metal: "Silver",
    category: "canada",
    mint: "Royal Canadian Mint"
  },

  // 2022 King Charles III Memorial Gold Coin
  {
    id: 215,
    name: "King Charles III Memorial 2oz Gold Coin",
    year: "2022",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$6,500",
    priceNum: 6500,
    image: "/assets/images/canadacoins/2022 King Charles III 'Queen Elizabeth II' Memorial 2oz Gold .png",
    description: "Memorial 2oz gold coin honoring Queen Elizabeth II with King Charles III portrait. Limited edition from RCM.",
    metal: "Gold",
    category: "canada",
    mint: "Royal Canadian Mint"
  },

  // 2024 Howling Wolf 2oz Silver Coin
  {
    id: 216,
    name: "The Howling Wolf 2oz Silver Coin",
    year: "2024",
    grade: "Proof 70",
    gradeNum: 70,
    price: "$180",
    priceNum: 180,
    image: "/assets/images/canadacoins/2024 Canadian 2 oz Silver $30 The Howling Wolf.png",
    description: "Stunning 2oz silver coin featuring a howling wolf design with antique finish. Pure silver from RCM.",
    metal: "Silver",
    category: "canada",
    mint: "Royal Canadian Mint"
  }
];

const CanadaPage = () => {
  const theme = useTheme();
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [filter, setFilter] = useState('all');

  const handlePriceInquiry = (coin) => {
    setSelectedCoin(coin);
    setFormOpen(true);
  };

  const filteredCoins = filter === 'all' 
    ? canadianCoins 
    : filter === 'diamond' 
      ? canadianCoins.filter(c => c.hasDiamond)
      : filter === 'gold'
        ? canadianCoins.filter(c => c.metal === 'Gold')
        : filter === 'silver'
          ? canadianCoins.filter(c => c.metal === 'Silver')
          : canadianCoins;

  // Calculate stats
  const goldCount = canadianCoins.filter(c => c.metal === 'Gold').length;
  const silverCount = canadianCoins.filter(c => c.metal === 'Silver').length;
  const diamondCount = canadianCoins.filter(c => c.hasDiamond).length;
  const totalCoins = canadianCoins.length;

  return (
    <Box sx={{ 
      py: 8,
      backgroundColor: '#F5F5F5',
      minHeight: '100vh',
    }}>
      <Container maxWidth="xl">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 4 }}>
          <MuiLink
            component={Link}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </MuiLink>
          <Typography color="primary">Canada Collection</Typography>
        </Breadcrumbs>

        {/* Header Section with Animation */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                mb: 2,
              }}
            >
              <ForestIcon sx={{ color: 'primary.main', fontSize: 40 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'primary.main',
                  letterSpacing: '0.3em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                }}
              >
                Canada
              </Typography>
            </Box>
            
            <Typography variant="h2" sx={{ mb: 3, color: '#2D2D2D' }}>
              Royal Canadian Mint
              <Box component="span" sx={{ 
                color: 'primary.main', 
                display: 'block',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                mt: 1
              }}>
                Collector's Coins
              </Box>
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                maxWidth: '700px', 
                mx: 'auto',
                color: '#616161',
                fontSize: '1.1rem',
                lineHeight: 1.8
              }}
            >
              Discover exceptional coins from the Royal Canadian Mint, featuring the iconic Maple Leaf, 
              diamond-dust finishes, and classic numismatic treasures. Each coin is certified and authenticated.
            </Typography>
          </motion.div>
        </Box>

        {/* Quick Stats */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Paper sx={{ px: 3, py: 1.5, textAlign: 'center', backgroundColor: alpha('#C6A75E', 0.05) }}>
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>{totalCoins}</Typography>
            <Typography variant="body2" color="#616161">Total Coins</Typography>
          </Paper>
          <Paper sx={{ px: 3, py: 1.5, textAlign: 'center', backgroundColor: alpha('#C6A75E', 0.05) }}>
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>{goldCount}</Typography>
            <Typography variant="body2" color="#616161">Gold Coins</Typography>
          </Paper>
          <Paper sx={{ px: 3, py: 1.5, textAlign: 'center', backgroundColor: alpha('#C6A75E', 0.05) }}>
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>{silverCount}</Typography>
            <Typography variant="body2" color="#616161">Silver Coins</Typography>
          </Paper>
          <Paper sx={{ px: 3, py: 1.5, textAlign: 'center', backgroundColor: alpha('#C6A75E', 0.05) }}>
            <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>{diamondCount}</Typography>
            <Typography variant="body2" color="#616161">Diamond/Gilded</Typography>
          </Paper>
        </Box>

        {/* Filter Chips */}
        <Stack 
          direction="row" 
          spacing={2} 
          justifyContent="center" 
          sx={{ mb: 6 }}
          flexWrap="wrap"
          gap={1}
        >
          <Chip
            label={`All Coins (${totalCoins})`}
            onClick={() => setFilter('all')}
            color={filter === 'all' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              py: 2,
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`Diamond/Gilded (${diamondCount})`}
            onClick={() => setFilter('diamond')}
            color={filter === 'diamond' ? 'primary' : 'default'}
            icon={<DiamondIcon />}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`Gold (${goldCount})`}
            onClick={() => setFilter('gold')}
            color={filter === 'gold' ? 'primary' : 'default'}
            icon={<AttachMoneyIcon />}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
          <Chip
            label={`Silver (${silverCount})`}
            onClick={() => setFilter('silver')}
            color={filter === 'silver' ? 'primary' : 'default'}
            sx={{ 
              fontSize: '0.9rem',
              '&.MuiChip-colorPrimary': {
                backgroundColor: 'primary.main',
                color: '#000000',
                fontWeight: 600,
              }
            }}
          />
        </Stack>

        {/* Results Count */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" color="#616161">
            Showing {filteredCoins.length} Canadian {filteredCoins.length === 1 ? 'coin' : 'coins'}
          </Typography>
          <Typography variant="body2" color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FilterListIcon sx={{ fontSize: 16 }} /> 
            {filter === 'all' ? 'All Coins' : `${filter.charAt(0).toUpperCase() + filter.slice(1)}`}
          </Typography>
        </Box>

        {/* Coins Grid */}
        {filteredCoins.length > 0 ? (
          <Grid 
            container 
            spacing={4} 
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {filteredCoins.map((coin, index) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                key={coin.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <CoinCard 
                    coin={coin} 
                    onPriceInquiry={() => handlePriceInquiry(coin)}
                  />
                </motion.div>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="#616161">
              No coins match the selected filter.
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => setFilter('all')}
              sx={{ mt: 2 }}
            >
              View All Coins
            </Button>
          </Box>
        )}

        {/* Back Button */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Button
            component={Link}
            to="/"
            variant="outlined"
            size="large"
            startIcon={<ArrowBackIcon />}
            sx={{ 
              px: 6, 
              py: 1.5,
              borderColor: alpha('#9E9E9E', 0.3),
              color: '#616161',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
              }
            }}
          >
            Back to Home
          </Button>
        </Box>

        {/* Info Box */}
        <Paper
          sx={{
            mt: 8,
            p: 4,
            backgroundColor: alpha('#C6A75E', 0.05),
            border: '1px solid',
            borderColor: alpha('#C6A75E', 0.2),
            borderRadius: 2,
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ color: '#2D2D2D', fontWeight: 600, mb: 2 }}>
            About Royal Canadian Mint Coins
          </Typography>
          <Typography variant="body1" color="#616161" paragraph sx={{ lineHeight: 1.8 }}>
            The Royal Canadian Mint is renowned for producing some of the world's finest collector coins, 
            featuring innovative security features and exceptional craftsmanship. Established in 1908, the mint
            has earned a global reputation for quality and innovation.
          </Typography>
          <Typography variant="body1" color="#616161" paragraph sx={{ lineHeight: 1.8 }}>
            Our Canadian collection includes proof specimens, diamond-dust finishes, gilded editions, classic 
            numismatic treasures, and historic gold pieces. Each coin is certified by PCGS or NGC and comes 
            with full provenance documentation.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
            {['PCGS Certified', 'NGC Certified', 'Provenance Included', 'Insured Shipping', 'Diamond Dust Finish', 'Pure Silver'].map((item) => (
              <Chip 
                key={item}
                label={item}
                size="small"
                sx={{ 
                  backgroundColor: alpha('#C6A75E', 0.1),
                  color: '#616161',
                  fontWeight: 500
                }}
              />
            ))}
          </Box>
        </Paper>
      </Container>

      {/* Application Form */}
      <ApplicationForm 
        open={formOpen}
        onClose={() => setFormOpen(false)}
        coin={selectedCoin}
      />
    </Box>
  );
};
// At the very bottom of CanadaPage.jsx, add:
export { canadianCoins };
export default CanadaPage;